export async function cropImage(
    base64: string,
    direction: 'up' | 'down' | 'left' | 'right',
    pixels: number,
): Promise<string> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = async () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                reject('Could not create canvas context');
                return;
            }

            const width = img.width;
            const height = img.height;

            let cropX = 0,
                cropY = 0,
                cropWidth = width,
                cropHeight = height;

            if (direction === 'up') {
                cropY = pixels;
                cropHeight -= pixels;
            } else if (direction === 'down') {
                cropHeight -= pixels;
            } else if (direction === 'left') {
                cropX = pixels;
                cropWidth -= pixels;
            } else if (direction === 'right') {
                cropWidth -= pixels;
            }

            canvas.width = cropWidth;
            canvas.height = cropHeight;

            ctx.drawImage(img, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);

            resolve(canvas.toDataURL());
        };

        img.onerror = () => reject('Failed to load image');

        img.src = base64;
    });
}

export async function cropWebsiteFromScreenshot(base64Image: string): Promise<string> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous'; // Prevent CORS issues
        img.src = base64Image;

        img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                reject(new Error('Canvas context not supported'));
                return;
            }

            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);

            const imageData = ctx.getImageData(0, 0, img.width, img.height);
            const pixels = imageData.data;

            const tolerance = 20; // Color similarity tolerance
            const edgeThreshold = 50; // Skip first few pixels to ignore OS window borders

            function getPixel(x: number, y: number): [number, number, number, number] {
                const index = (y * img.width + x) * 4;
                return [pixels[index], pixels[index + 1], pixels[index + 2], pixels[index + 3]]; // RGBA
            }

            function isSimilarColor(color1: number[], color2: number[], tol: number): boolean {
                return (
                    Math.abs(color1[0] - color2[0]) < tol &&
                    Math.abs(color1[1] - color2[1]) < tol &&
                    Math.abs(color1[2] - color2[2]) < tol
                );
            }

            // Detect top border
            let top = 0;
            const firstRowColor = getPixel(edgeThreshold, edgeThreshold);
            for (let y = edgeThreshold; y < img.height / 3; y++) {
                const currentColor = getPixel(edgeThreshold, y);
                if (!isSimilarColor(currentColor, firstRowColor, tolerance)) {
                    top = y;
                    break;
                }
            }

            // Adjust canvas to only show detected website content
            const newWidth = img.width;
            const newHeight = img.height - top;

            const croppedCanvas = document.createElement('canvas');
            croppedCanvas.width = newWidth;
            croppedCanvas.height = newHeight;
            const croppedCtx = croppedCanvas.getContext('2d');

            if (!croppedCtx) {
                reject(new Error('Failed to get cropped canvas context'));
                return;
            }

            croppedCtx.drawImage(img, 0, top, newWidth, newHeight, 0, 0, newWidth, newHeight);

            // Convert cropped canvas to Base64
            const croppedBase64 = croppedCanvas.toDataURL('image/png');

            resolve(croppedBase64);
        };

        img.onerror = () => reject(new Error('Failed to load image'));
    });
}

export async function constrainScreenshotSize(
    base64Image: string,
    maxDataUrlLength = 2_800_000,
): Promise<string> {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => {
            const scale = Math.min(1, 1800 / Math.max(image.width, image.height));
            const canvas = document.createElement('canvas');
            canvas.width = Math.max(1, Math.round(image.width * scale));
            canvas.height = Math.max(1, Math.round(image.height * scale));
            const context = canvas.getContext('2d');
            if (!context) {
                reject(new Error('Canvas context not supported'));
                return;
            }

            context.drawImage(image, 0, 0, canvas.width, canvas.height);

            let quality = 0.9;
            let result = canvas.toDataURL('image/jpeg', quality);
            while (result.length > maxDataUrlLength && quality > 0.5) {
                quality -= 0.1;
                result = canvas.toDataURL('image/jpeg', quality);
            }

            if (result.length > maxDataUrlLength) {
                const reduction = Math.sqrt(maxDataUrlLength / result.length) * 0.9;
                const smallerCanvas = document.createElement('canvas');
                smallerCanvas.width = Math.max(1, Math.round(canvas.width * reduction));
                smallerCanvas.height = Math.max(1, Math.round(canvas.height * reduction));
                const smallerContext = smallerCanvas.getContext('2d');
                if (!smallerContext) {
                    reject(new Error('Canvas context not supported'));
                    return;
                }
                smallerContext.drawImage(canvas, 0, 0, smallerCanvas.width, smallerCanvas.height);
                result = smallerCanvas.toDataURL('image/jpeg', 0.75);
            }

            if (result.length > maxDataUrlLength) {
                reject(new Error('Screenshot is too large to attach'));
                return;
            }

            resolve(result);
        };
        image.onerror = () => reject(new Error('Failed to load image'));
        image.src = base64Image;
    });
}
